import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { ITraining, BaseVideoFormData } from '../../types';
import { apiService } from '../../api/api.service';

// State interface
interface PresentationState {
    videos: ITraining[];
    selectedVideo: ITraining | null;
    isLoading: boolean;
    error: string | null;
    deleteDialog: {
        open: boolean;
        videoId: number | null;
    };
    openDialog: boolean;
}

// Initial state
const initialState: PresentationState = {
    videos: [],
    selectedVideo: null,
    isLoading: false,
    error: null,
    deleteDialog: {
        open: false,
        videoId: null
    },
    openDialog: false
};

// Async thunks
export const fetchPresentationVideos = createAsyncThunk(
    'presentation/fetch',
    async (language: string, { rejectWithValue }) => {
        try {
            const data = await apiService.getVideoList('presentation');
            const filteredVideos = data.filter(
                video => video.language && video.language.toUpperCase() === language.toUpperCase()
            );
            return filteredVideos;
        } catch (error) {
            return rejectWithValue('Failed to fetch presentation videos');
        }
    }
);

export const savePresentationVideo = createAsyncThunk(
    'presentation/save',
    async ({
        data,
        language,
        videoFile,
        thumbnailFile
    }: {
        data: BaseVideoFormData;
        language: string,
        videoFile?: File,
        thumbnailFile?: File
    }, { getState, rejectWithValue }) => {
        try {
            const state = getState() as { presentation: PresentationState };
            const { videos } = state.presentation;

            let video_file_path = undefined;
            let preview_file_path = undefined;

            // Загружаем видео файл, если он есть
            if (videoFile) {
                const videoResponse = await apiService.uploadPresentationVideoFile(videoFile);
                video_file_path = videoResponse.file_path;
            }

            // Загружаем превью файл, если он есть
            if (thumbnailFile) {
                const previewResponse = await apiService.uploadPresentationPreviewFile(thumbnailFile);
                preview_file_path = previewResponse.file_path;
            }

            const trainingWithMetadata = {
                ...data.training,
                language: language.toUpperCase(),
                video_file_path,
                preview_file_path
            };

            if (videos.length > 0) {
                const response = await apiService.editVideo('presentation', videos[0].id, trainingWithMetadata);
                return response;
            } else {
                const response = await apiService.uploadVideo('presentation', trainingWithMetadata);
                return response;
            }
        } catch (error) {
            return rejectWithValue('Failed to save presentation video');
        }
    }
);

export const deletePresentationVideo = createAsyncThunk(
    'presentation/delete',
    async (id: number, { rejectWithValue }) => {
        try {
            await apiService.deleteVideo('presentation', id);
            return id;
        } catch (error) {
            return rejectWithValue('Failed to delete presentation video');
        }
    }
);

// Slice
const presentationSlice = createSlice({
    name: 'presentation',
    initialState,
    reducers: {
        setOpenDialog: (state, action: PayloadAction<boolean>) => {
            state.openDialog = action.payload;
        },
        setSelectedVideo: (state, action: PayloadAction<ITraining | null>) => {
            state.selectedVideo = action.payload;
        },
        setDeleteDialog: (state, action: PayloadAction<{ open: boolean; videoId: number | null }>) => {
            state.deleteDialog = action.payload;
        },
        updateVideoFromStatus: (state, action: PayloadAction<{
            videoId: number;
            updates: Partial<ITraining>
        }>) => {
            const { videoId, updates } = action.payload;

            const videoIndex = state.videos.findIndex(v => v.id === videoId);
            if (videoIndex !== -1) {
                state.videos[videoIndex] = {
                    ...state.videos[videoIndex],
                    ...updates
                };
            }
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch presentation videos
            .addCase(fetchPresentationVideos.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchPresentationVideos.fulfilled, (state, action) => {
                state.isLoading = false;
                state.videos = action.payload;
            })
            .addCase(fetchPresentationVideos.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })

            // Save presentation video
            .addCase(savePresentationVideo.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(savePresentationVideo.fulfilled, (state, action) => {
                state.isLoading = false;
                state.videos = [action.payload];
                state.openDialog = false;
            })
            .addCase(savePresentationVideo.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })

            // Delete presentation video
            .addCase(deletePresentationVideo.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(deletePresentationVideo.fulfilled, (state) => {
                state.isLoading = false;
                state.videos = [];
                state.deleteDialog = {
                    open: false,
                    videoId: null
                };
            })
            .addCase(deletePresentationVideo.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
                state.deleteDialog = {
                    open: false,
                    videoId: null
                };
            });
    }
});

export const { setOpenDialog, setSelectedVideo, setDeleteDialog, updateVideoFromStatus } = presentationSlice.actions;
export default presentationSlice.reducer; 